import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { CHAIN_PATH, refEntries, refId, refLabel, type ChainRef } from './chainRef';

/**
 * The document chain, as a navigation rail.
 *
 * Every document in this platform already stores its neighbours' ids and the
 * routes to reach them already exist, but nothing rendered a link: the client
 * invoice screen, for one, prints "Confirmed order: QUO-00015" as dead text
 * while holding `quotationId` in the same object. So a person answering "which
 * vessel asked for this, and what did we pay" has been retyping numbers into
 * search boxes to walk a chain the data already describes.
 *
 * Two decisions worth knowing about:
 *
 * 1. MISSING HOPS ARE RENDERED, not hidden. A rail reading
 *    `RFQ > QUO > Order > -- > --` is telling you that order has no purchase
 *    order raised and has never been invoiced. Nothing else in the product
 *    answers that: `ConfirmedOrder.status` is only ever `confirmed` or
 *    `cancelled` and `VendorPurchaseOrder.status` is only `draft`, `sent`,
 *    `amended` or `cancelled` (D81), so there is no lifecycle state to query and no screen that lists what is outstanding.
 *    Showing the gap is the cheapest honest version of that, and it costs a
 *    complete chain nothing.
 *
 *    This only works if the rail can tell "there is no purchase order" from "I
 *    was not told about purchase orders". An OMITTED prop is unknown and says
 *    so; `null` or `[]` is a positive statement that the hop is empty. Missing
 *    is not zero — the same rule `utils/num.js` enforces on the server, and for
 *    the same reason: a screen that has not loaded the downstream documents
 *    must not announce that they do not exist.
 *
 * 2. A HOP WITH NO ROUTE IS A CHIP, NOT A DEAD LINK. Vendor invoices have no
 *    detail route -- `/vendor-invoices` is a list that reads no URL parameters
 *    -- so that hop renders the invoice number as plain text. It becomes a link
 *    the day the route exists, by adding `to` to its stage below and nothing
 *    else.
 *
 * Permissions are honoured per hop rather than for the rail as a whole: a user
 * who can see invoices but not purchase orders gets a readable chain with the
 * PO hop inert, instead of a rail that vanishes.
 */

export type ChainStage =
  | 'rfq'
  | 'quotation'
  | 'confirmedOrder'
  | 'purchaseOrder'
  | 'vendorInvoice'
  | 'clientInvoice';

export interface ChainRailProps {
  /** Which hop the reader is currently looking at. Rendered inert. */
  current: ChainStage;

  rfq?: ChainRef;
  rfqNumber?: string | null;

  quotation?: ChainRef;
  quotationNumber?: string | null;

  confirmedOrder?: ChainRef;
  confirmedOrderNumber?: string | null;

  /** Every purchase order raised against this order. */
  purchaseOrders?: ChainRef[] | null;

  /** Vendor invoices matched to those purchase orders. */
  vendorInvoices?: ChainRef[] | null;

  clientInvoice?: ChainRef;
  clientInvoiceNumber?: string | null;

  className?: string;

  /** Called on a click on a hop's link, before it navigates. A page with unsaved edits uses it to ask first (preventDefault stops the link). */
  onNavigate?: (e: React.MouseEvent, to: string) => void;
  /**
   * Leave out hops with nothing behind them, and render nothing at all when no hop is linked.
   * Opt-in: the default keeps the gaps visible (see the header note); the client invoice header
   * has its own title and opts in so a manual invoice is not drawn as a row of empty boxes.
   */
  hideEmpty?: boolean;
}

/**
 * Beyond this the rail collapses, matching the "first 3 plus +N" convention the
 * Source Trace panel already uses — but the overflow is a BUTTON that reveals
 * the rest, never a dead count.
 *
 * One confirmed order legitimately fans out to many suppliers: the ask is split
 * by `selectedVendor` and a purchase order is raised per vendor, so ten POs
 * against one order and ten against one client invoice are both real shapes in
 * the data today. Summarising them behind an inert "+7" would make seven
 * purchase orders unreachable from the invoice that paid for them — the exact
 * defect QA_TEST_CASES_VENDOR_TRACEABILITY TC-VT-012 was written for, where
 * anything past the third was lost permanently.
 */
const MAX_INLINE = 3;

type ScreenKey = 'quotations' | 'confirmed_orders' | 'vendor_purchase_orders' | 'invoices';

interface StageSpec {
  key: ChainStage;
  /** Shown when the hop is present but unnamed, and used in every tooltip. */
  noun: string;
  screen: ScreenKey;
  /** Absent for hops that have no detail route yet. */
  to?: (id: string) => string;
  /** Why this hop being empty is worth reading, phrased for an operator. */
  absent: string;
  /** Fields to try, in order, when a populated reference arrives. */
  labelKeys: readonly string[];
}

const STAGES: readonly StageSpec[] = [
  {
    key: 'rfq',
    noun: 'RFQ',
    screen: 'quotations',
    // The inbox honours `?selected=` and opens the request with its source
    // email and thread beside it, so this hop is also "back to the email
    // this came in on". Precedent: QuotationTable and the vendor PO header.
    to: CHAIN_PATH.rfq,
    absent: 'No RFQ — this was created manually rather than from a request.',
    labelKeys: ['rfqId', 'rfqNumber', 'subject'],
  },
  {
    key: 'quotation',
    noun: 'Quotation',
    screen: 'quotations',
    to: CHAIN_PATH.quotation,
    absent: 'No quotation — nothing was quoted for this.',
    labelKeys: ['quotationNumber'],
  },
  {
    key: 'confirmedOrder',
    noun: 'Order',
    screen: 'confirmed_orders',
    to: CHAIN_PATH.confirmedOrder,
    absent: 'Not confirmed — the client has not accepted this quotation.',
    labelKeys: ['orderNumber', 'quotationNumber', 'rfqNumber'],
  },
  {
    key: 'purchaseOrder',
    noun: 'Purchase order',
    screen: 'vendor_purchase_orders',
    to: CHAIN_PATH.purchaseOrder,
    absent: 'No purchase order raised — nothing has been ordered from a supplier.',
    labelKeys: ['poNumber', 'vendorName'],
  },
  {
    key: 'vendorInvoice',
    noun: 'Vendor invoice',
    screen: 'invoices',
    // No detail route exists. See the header note.
    absent: 'No vendor invoice linked — the supplier’s bill has not been matched to the purchase order.',
    labelKeys: ['invoiceNumber', 'vendorName'],
  },
  {
    key: 'clientInvoice',
    noun: 'Client invoice',
    screen: 'invoices',
    to: CHAIN_PATH.clientInvoice,
    absent: 'Not invoiced — the client has not been billed for this order.',
    labelKeys: ['invoiceNumber'],
  },
];

const CHIP_BASE =
  'inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-xs sm:text-xs ' +
  'max-w-[150px] sm:max-w-[190px] truncate align-middle';

const CHIP_LINK =
  `${CHIP_BASE} bg-slate-100 text-cyan-700 hover:bg-slate-200 hover:underline ` +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-1';

const CHIP_STATIC = `${CHIP_BASE} bg-slate-100 text-slate-600`;

const CHIP_CURRENT = `${CHIP_BASE} bg-slate-700 text-white`;

const CHIP_ABSENT =
  `${CHIP_BASE} border border-dashed border-slate-300 bg-transparent text-slate-400`;

/** Quieter than absent, because it is a smaller claim: this screen simply did not ask. */
const CHIP_UNKNOWN = `${CHIP_BASE} bg-transparent text-slate-300`;

/** The overflow disclosure. Looks interactive because it is. */
const CHIP_BUTTON =
  `${CHIP_BASE} bg-slate-200 text-slate-700 hover:bg-slate-300 cursor-pointer ` +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-1';

interface Entry {
  id: string;
  label: string | null;
}

export function ChainRail({
  current,
  rfq,
  rfqNumber,
  quotation,
  quotationNumber,
  confirmedOrder,
  confirmedOrderNumber,
  purchaseOrders,
  vendorInvoices,
  clientInvoice,
  clientInvoiceNumber,
  className = '',
  hideEmpty = false,
  onNavigate,
}: ChainRailProps) {
  // Hooks are called unconditionally and in a fixed order, one per screen the
  // rail can reach. Deriving them inside the stage loop would make the hook
  // count depend on the data.
  const quotationsPerm = usePermission('quotations');
  const confirmedOrdersPerm = usePermission('confirmed_orders');
  const purchaseOrdersPerm = usePermission('vendor_purchase_orders');
  const invoicesPerm = usePermission('invoices');

  const canView: Record<ScreenKey, boolean> = useMemo(
    () => ({
      quotations: quotationsPerm.canView,
      confirmed_orders: confirmedOrdersPerm.canView,
      vendor_purchase_orders: purchaseOrdersPerm.canView,
      invoices: invoicesPerm.canView,
    }),
    [
      quotationsPerm.canView,
      confirmedOrdersPerm.canView,
      purchaseOrdersPerm.canView,
      invoicesPerm.canView,
    ]
  );

  const entriesByStage: Record<ChainStage, Entry[]> = useMemo(() => {
    const single = (
      ref: ChainRef,
      fallback: string | null | undefined,
      labelKeys: readonly string[]
    ): Entry[] => {
      const id = refId(ref);
      if (!id) return [];
      const label = refLabel(ref, labelKeys) ?? (fallback ? String(fallback).trim() : null);
      return [{ id, label: label || null }];
    };

    return {
      rfq: single(rfq, rfqNumber, STAGES[0].labelKeys),
      quotation: single(quotation, quotationNumber, STAGES[1].labelKeys),
      confirmedOrder: single(confirmedOrder, confirmedOrderNumber, STAGES[2].labelKeys),
      purchaseOrder: refEntries(purchaseOrders, STAGES[3].labelKeys),
      vendorInvoice: refEntries(vendorInvoices, STAGES[4].labelKeys),
      clientInvoice: single(clientInvoice, clientInvoiceNumber, STAGES[5].labelKeys),
    };
  }, [
    rfq,
    rfqNumber,
    quotation,
    quotationNumber,
    confirmedOrder,
    confirmedOrderNumber,
    purchaseOrders,
    vendorInvoices,
    clientInvoice,
    clientInvoiceNumber,
  ]);

  // `undefined` means the caller never mentioned this hop, so the rail must not
  // claim the document does not exist. Anything else — including null and [] —
  // is the caller positively stating the hop is empty.
  const knownByStage: Record<ChainStage, boolean> = useMemo(
    () => ({
      rfq: rfq !== undefined,
      quotation: quotation !== undefined,
      confirmedOrder: confirmedOrder !== undefined,
      purchaseOrder: purchaseOrders !== undefined,
      vendorInvoice: vendorInvoices !== undefined,
      clientInvoice: clientInvoice !== undefined,
    }),
    [rfq, quotation, confirmedOrder, purchaseOrders, vendorInvoices, clientInvoice]
  );

  const stages = hideEmpty ? STAGES.filter((st) => entriesByStage[st.key].length > 0 || st.key === current) : STAGES;
  if (hideEmpty && !STAGES.some((st) => st.key !== current && entriesByStage[st.key].length > 0)) return null;

  return (
    <nav
      aria-label="Document chain"
      className={`min-w-0 ${className}`.trim()}
      data-testid="chain-rail"
    >
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 m-0 p-0 list-none">
        {stages.map((stage, index) => {
          const entries = entriesByStage[stage.key];
          const isCurrent = stage.key === current;
          const permitted = canView[stage.screen];

          return (
            <li key={stage.key} className="flex items-center gap-x-1.5 min-w-0">
              {index > 0 ? (
                <span aria-hidden="true" className="text-slate-300 select-none">
                  ›
                </span>
              ) : null}

              {renderStage({
                stage,
                entries,
                isCurrent,
                permitted,
                known: knownByStage[stage.key],
                onNavigate,
              })}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function renderStage({
  stage,
  entries,
  isCurrent,
  permitted,
  known,
  onNavigate,
}: {
  stage: StageSpec;
  entries: Entry[];
  isCurrent: boolean;
  permitted: boolean;
  known: boolean;
  onNavigate?: ChainRailProps['onNavigate'];
}) {
  if (entries.length === 0) {
    if (isCurrent) {
      return (
        <span
          className={CHIP_CURRENT}
          title={`${stage.noun} — you are here`}
          aria-label={`${stage.noun}, current`}
          aria-current="step"
        >
          {stage.noun}
        </span>
      );
    }

    // Unknown is not empty. Saying "no purchase order raised" on a screen that
    // never asked about purchase orders would be a confident wrong answer to
    // the one question this rail exists to answer.
    if (!known) {
      return (
        <span
          className={CHIP_UNKNOWN}
          title={`${stage.noun}: not loaded on this screen`}
          aria-label={`${stage.noun}: not loaded`}
        >
          ·
        </span>
      );
    }

    // The gap is the message. `title` carries the operator-facing reason and
    // aria-label repeats it, because an em dash reads as nothing to a screen
    // reader.
    return (
      <span
        className={CHIP_ABSENT}
        title={`${stage.noun}: ${stage.absent}`}
        aria-label={`${stage.noun}: none`}
      >
        —
      </span>
    );
  }

  return <StageEntries stage={stage} entries={entries} isCurrent={isCurrent} permitted={permitted} onNavigate={onNavigate} />;
}

/**
 * The chips for one hop, with the overflow disclosure.
 *
 * Its own component purely so the expanded/collapsed state is per hop: opening
 * the purchase orders on an invoice must not also expand its vendor invoices.
 */
function StageEntries({
  stage,
  entries,
  isCurrent,
  permitted,
  onNavigate,
}: {
  stage: StageSpec;
  entries: Entry[];
  isCurrent: boolean;
  permitted: boolean;
  onNavigate?: ChainRailProps['onNavigate'];
}) {
  const [expanded, setExpanded] = useState(false);

  const collapsible = entries.length > MAX_INLINE;
  const shown = expanded || !collapsible ? entries : entries.slice(0, MAX_INLINE);
  const hidden = entries.length - shown.length;
  const plural = `${stage.noun.toLowerCase()}${entries.length === 1 ? '' : 's'}`;

  return (
    <span className="inline-flex items-center gap-1 min-w-0 flex-wrap">
      {shown.map((entry) => {
        const text = entry.label || stage.noun;
        const fullTitle = `${stage.noun}: ${entry.label || entry.id}`;

        if (isCurrent) {
          return (
            <span
              key={entry.id}
              className={CHIP_CURRENT}
              title={`${fullTitle} — you are here`}
              aria-current="step"
            >
              {text}
            </span>
          );
        }

        if (!permitted) {
          return (
            <span
              key={entry.id}
              className={CHIP_STATIC}
              title={`${fullTitle} — you do not have access to this screen`}
            >
              {text}
            </span>
          );
        }

        if (!stage.to) {
          return (
            <span
              key={entry.id}
              className={CHIP_STATIC}
              title={`${fullTitle} — no detail screen exists for vendor invoices yet`}
            >
              {text}
            </span>
          );
        }

        return (
          <Link key={entry.id} to={stage.to(entry.id)} className={CHIP_LINK} title={fullTitle} onClick={(e) => onNavigate?.(e, stage.to!(entry.id))}>
            {text}
          </Link>
        );
      })}

      {collapsible ? (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? `Show fewer ${plural}`
              : `Show ${hidden} more ${plural}, ${entries.length} in total`
          }
          title={
            expanded
              ? `Showing all ${entries.length} ${plural}`
              : `${hidden} more ${plural} — click to show all ${entries.length}`
          }
          className={CHIP_BUTTON}
        >
          {expanded ? 'Show fewer' : `+${hidden}`}
        </button>
      ) : null}
    </span>
  );
}

export default ChainRail;
